import React, { useState, useCallback, useEffect, useRef } from 'react';
import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { ChevronLeftRegular } from '@fluentui/react-icons';
import ChatInterface from './ChatInterface';
import DocumentPanel from './DocumentPanel';
import HeaderControls from './HeaderControls';
import { DocumentInfo, ChatState, UserThread, ChatMessage, GradeData, TodoItem } from '../models/types';
import { normalizeUiMode } from '../utils/domain';
import { ExtractedLink } from '../utils/linkExtractor';
import { ChatAPI } from '../utils/api';

interface ChatContainerProps {
  uiMode?: string; // 'Authorizations' or 'Appeals' from segmented control
  onViewChange?: (view: string) => void; // Callback to change view based on thread domain
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden', // Changed from 'hidden' to allow tooltips
  },
  header: {
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden', // Changed from 'hidden' to allow tooltips
  },
  chatArea: {
    flex: 1,
    minWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
  },
  documentPanelContainer: {
    flexShrink: 0,
    overflow: 'visible',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  documentPanel: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0, // Don't shrink this panel
    minWidth: '300px',
    height: '100%',
    // No maxWidth - controlled by JavaScript resize logic
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    transition: 'width 0.3s ease-in-out',
    position: 'relative',
    zIndex: 10,
    overflow: 'visible', // Changed from 'hidden' to 'visible' to show toggle button
    '@media (max-width: 968px)': {
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      width: '100vw',
      maxWidth: '400px',
      zIndex: 100,
      boxShadow: tokens.shadow28,
      overflow: 'hidden', // Keep hidden on mobile
    },
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: '-4px',
    width: '8px',
    height: '100%',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    zIndex: 12,
    '@media (max-width: 968px)': {
      display: 'none', // Hide resize handle on mobile
    },
    ':hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
    '::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '2px',
      height: '40px',
      backgroundColor: tokens.colorNeutralStroke1,
      borderRadius: '1px',
    },
  },
  resizeHandleActive: {
    backgroundColor: tokens.colorBrandBackground,
    '::after': {
      backgroundColor: tokens.colorNeutralForegroundOnBrand,
    },
  },
  panelToggleButton: {
    position: 'absolute',
    top: '50%',
    left: '-45px', // Moved slightly further out to ensure visibility
    transform: 'translateY(-50%)',
    borderRadius: `${tokens.borderRadiusMedium} 0 0 ${tokens.borderRadiusMedium}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRight: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: '40px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 15, // Increased z-index to appear above resize handle and panel
    boxShadow: tokens.shadow8, // Add shadow for visibility
    '@media (max-width: 968px)': {
      display: 'none', // Hide on mobile since panel becomes overlay
    },
  },
  panelToggleButtonExternal: {
    position: 'fixed',
    top: '50%',
    right: '0px',
    transform: 'translateY(-50%)',
    borderRadius: `${tokens.borderRadiusMedium} 0 0 ${tokens.borderRadiusMedium}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRight: 'none',
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: '40px',
    height: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    boxShadow: tokens.shadow8,
    transition: 'all 0.3s ease-in-out',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateY(-50%) translateX(-4px)',
    },
    '@media (max-width: 968px)': {
      display: 'none', // Hide on mobile
    },
  },
  documentPanelHeader: {
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  documentPanelContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  documentViewerContainer: {
    flex: 1,
    minHeight: '200px',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  documentListContainer: {
    flex: '0 0 250px',
    minHeight: '200px',
    maxHeight: '400px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'auto',
  },
  documentPanelSplitter: {
    height: '4px',
    backgroundColor: tokens.colorNeutralStroke1,
    cursor: 'row-resize',
    flexShrink: 0,
    ':hover': {
      backgroundColor: tokens.colorBrandStroke1,
    },
  },
});

// Centralized application state interface
interface AppState {
  // Authorization & Thread State
  authorizationId: string;
  selectedThreadId: string | null;
  threads: UserThread[];
  threadsLoading: boolean;
  
  // Chat State
  chatState: ChatState;
  inputValue: string;
  streamingMessage: string;
  statusMessage: string;
  
  // Document State
  documents: DocumentInfo[];
  selectedDocument: DocumentInfo | null;
  selectedDocuments: string[]; // Array of selected document GUIDs
  documentsLoading: boolean;
  documentsError: string | null;
  conversationLinks: ExtractedLink[];
  
  // Grading State
  gradeData: GradeData[];
  gradingLoading: boolean;
  gradingError: string | null;
  
  // UI State
  isDocumentPanelOpen: boolean;
  panelWidth: number;
  isResizing: boolean;
  isLoadingConversation: boolean;
  loadingConversationError: string | null;
}

const SESSION_ID_STORAGE_PREFIX = 'aurora:chat-session:';

const getStoredSessionId = (threadId: string): string | undefined => {
  try {
    return window.sessionStorage.getItem(`${SESSION_ID_STORAGE_PREFIX}${threadId}`) || undefined;
  } catch {
    return undefined;
  }
};

const storeSessionId = (threadId: string, sessionId: string): void => {
  try {
    window.sessionStorage.setItem(`${SESSION_ID_STORAGE_PREFIX}${threadId}`, sessionId);
  } catch {
    // Keep the in-memory chat session usable when browser storage is unavailable.
  }
};

/**
 * ChatContainer - Main container component that manages all shared state
 * and coordinates between ChatInterface, DocumentPanel, and HeaderControls
 * 
 * This component follows MVVM pattern:
 * - Model: AppState and API calls
 * - View: Child components (ChatInterface, DocumentPanel, HeaderControls)
 * - ViewModel: This component with state management and event handlers
 * 
 * This is a demonstration of the improved architecture. The existing components
 * still use the event-driven pattern until they can be refactored.
 */
export const ChatContainer: React.FC<ChatContainerProps> = ({ 
  uiMode = 'Authorizations',
  onViewChange,
}) => {
  const classes = useStyles();
  
  console.log(`[ChatContainer] Component render with uiMode prop: ${uiMode}`);

  // Live todo list state — updated by agent write_todos tool calls during streaming
  const [todos, setTodos] = useState<TodoItem[]>([]);
  
  // Centralized application state
  const [appState, setAppState] = useState<AppState>({
    // Authorization & Thread State
    authorizationId: '',
    selectedThreadId: null,
    threads: [],
    threadsLoading: false,
    
    // Chat State
    chatState: {
      messages: [],
      isLoading: false,
      error: null,
      authNumber: '',
      threadId: undefined,
      sessionId: crypto.randomUUID(), // Generate session ID on component initialization
      aiServicesAvailable: false,
      isStreaming: false,
    },
    inputValue: '',
    streamingMessage: '',
    statusMessage: '',
    
    // Document State
    documents: [],
    selectedDocument: null,
    selectedDocuments: [],
    documentsLoading: false,
    documentsError: null,
    conversationLinks: [],
    
    // Grading State
    gradeData: [],
    gradingLoading: false,
    gradingError: null,
    
    // UI State
    isDocumentPanelOpen: false,
    panelWidth: 600,
    isResizing: false,
    isLoadingConversation: false,
    loadingConversationError: null,
  });

  // Refs for optimization
  const loadingRef = useRef<boolean>(false);
  const uiModeRef = useRef<string>(uiMode);
  uiModeRef.current = uiMode;
  const selectionGenerationRef = useRef(0);
  const selectionRef = useRef({ recordId: appState.authorizationId, mode: uiMode });

  const advanceSelection = useCallback((recordId: string, mode: string) => {
    if (selectionRef.current.recordId === recordId && selectionRef.current.mode === mode) {
      return selectionGenerationRef.current;
    }
    selectionRef.current = { recordId, mode };
    selectionGenerationRef.current += 1;
    return selectionGenerationRef.current;
  }, []);

  const isCurrentSelection = useCallback((recordId: string, mode: string, generation: number) =>
    selectionGenerationRef.current === generation &&
    selectionRef.current.recordId === recordId &&
    selectionRef.current.mode === mode &&
    uiModeRef.current === mode, []);

  useEffect(() => {
    advanceSelection(appState.authorizationId, uiMode);
  }, [advanceSelection, appState.authorizationId, uiMode]);

  useEffect(() => {
    const { threadId, sessionId } = appState.chatState;
    if (threadId && sessionId) {
      storeSessionId(threadId, sessionId);
    }
  }, [appState.chatState.sessionId, appState.chatState.threadId]);

  // ==================== AUTHORIZATION MANAGEMENT ====================
  
  const handleAuthorizationChange = async (newAuthId: string, shouldLoadDocuments = false) => {
    console.log(`[ChatContainer] Authorization change: "${newAuthId}", loadDocs: ${shouldLoadDocuments}`);
    const selectionMode = uiModeRef.current;
    const selectionGeneration = advanceSelection(newAuthId, selectionMode);
    
    setAppState(prev => ({
      ...prev,
      authorizationId: newAuthId,
      chatState: {
        ...prev.chatState,
        authNumber: newAuthId,
        // Clear chat messages when authorization changes
        messages: [],
        threadId: undefined,
        sessionId: crypto.randomUUID(), // Generate new session ID when authorization changes
        error: null,
        isLoading: false,
        isStreaming: false,
      },
      // Clear thread selection when authorization changes
      selectedThreadId: null,
      // Clear input and streaming state
      inputValue: '',
      streamingMessage: '',
      statusMessage: '',
      // Clear conversation state for new authorization
      ...(shouldLoadDocuments ? {
        documents: [],
        selectedDocument: null,
        selectedDocuments: [], // Clear selected documents
        documentsLoading: false,
        documentsError: null,
        conversationLinks: [],
      } : {}),
      // Clear grading data when authorization changes
      gradeData: [],
      gradingLoading: false,
      gradingError: null,
    }));
    setTodos([]);

    // Load documents if requested and authorization is provided
    if (shouldLoadDocuments && newAuthId.trim()) {
      try {
        await ChatAPI.warmupRecord(newAuthId.trim(), normalizeUiMode(selectionMode));
      } catch (error) {
        console.warn('[ChatContainer] Record warm-up was incomplete:', error);
      }
      if (!isCurrentSelection(newAuthId, selectionMode, selectionGeneration)) return;
      await loadDocuments(newAuthId, true, selectionGeneration, selectionMode);
    } else if (!newAuthId.trim()) {
      // Clear documents when authorization is cleared
      setAppState(prev => ({
        ...prev,
        documents: [],
        selectedDocument: null,
        selectedDocuments: [], // Clear selected documents
        documentsLoading: false,
        documentsError: null,
        conversationLinks: [],
        // Clear grading data when authorization is cleared
        gradeData: [],
        gradingLoading: false,
        gradingError: null,
        isDocumentPanelOpen: false,
      }));
      setTodos([]);
    }
  };

  // ==================== THREAD MANAGEMENT ====================
  
  const loadUserThreads = async (force: boolean = false): Promise<UserThread[]> => {
    // Prevent duplicate calls unless forced
    if (!force && (appState.threadsLoading || loadingRef.current)) {
      console.log('[ChatContainer] Threads already loading or loaded, skipping');
      return appState.threads;
    }

    console.log('[ChatContainer] Loading user threads', force ? '(forced)' : '');
    setAppState(prev => ({
      ...prev,
      threadsLoading: true,
    }));

    try {
      const response = await ChatAPI.getUserThreads();
      const threads = (response.threads || []).sort((a, b) => {
        const aTs = a.LastUsedAt || a.CreatedAt || '';
        const bTs = b.LastUsedAt || b.CreatedAt || '';
        return new Date(bTs).getTime() - new Date(aTs).getTime();
      });
      
      setAppState(prev => ({
        ...prev,
        threads,
        threadsLoading: false,
      }));
      
      return threads;
    } catch (error) {
      console.error('[ChatContainer] Failed to load threads:', error);
      setAppState(prev => ({
        ...prev,
        threadsLoading: false,
      }));
      return [];
    }
  };

  // Handle thread selection
  const handleThreadSelect = useCallback(async (threadId: string | null, authorizationId?: string) => {
    console.log(`[ChatContainer] Thread select: ${threadId}, authId: ${authorizationId}`);
    
    if (!threadId) {
      advanceSelection('', uiModeRef.current);
      // Clear thread selection - start new conversation
      console.log('[ChatContainer] Clearing thread selection and starting new conversation');
      setAppState(prev => ({
        ...prev,
        selectedThreadId: null,
        authorizationId: '', // Clear authorization ID for new conversation
        chatState: {
          ...prev.chatState,
          messages: [],
          threadId: undefined,
          sessionId: crypto.randomUUID(), // Generate new session ID for new conversation
          authNumber: '', // Clear auth number in chat state
          error: null,
        },
        // Clear documents and links for new conversation
        documents: [],
        selectedDocument: null,
        selectedDocuments: [], // Clear selected documents
        documentsLoading: false,
        documentsError: null,
        conversationLinks: [],
        // Clear grading data for new conversation
        gradeData: [],
        gradingLoading: false,
        gradingError: null,
        isLoadingConversation: false,
        loadingConversationError: null,
        isDocumentPanelOpen: false, // Close document panel for new conversation
      }));
      setTodos([]);
      console.log('[ChatContainer] Cleared grading data for new conversation');
      return;
    }

    let selectionGeneration = advanceSelection(authorizationId || '', uiModeRef.current);

    // Extract domain from selected thread and trigger view change if needed
    const selectedThread = appState.threads.find(t => t.ThreadId === threadId);
    if (selectedThread?.Domain && onViewChange) {
      // Map thread domain to view: "authorization" -> "Authorizations", "appeals" -> "Appeals"
      const targetView = selectedThread.Domain.toLowerCase() === 'appeals' 
        ? 'Appeals' 
        : 'Authorizations';
      
      console.log(`[ChatContainer] Thread domain: ${selectedThread.Domain}, switching to view: ${targetView}`);
      
      // Only change view if different from current
      if (targetView !== uiMode) {
        console.log(`[ChatContainer] Triggering view change from ${uiMode} to ${targetView}`);
        onViewChange(targetView);
        // Small delay to allow view change to propagate before loading conversation
        await new Promise(resolve => setTimeout(resolve, 100));
        selectionGeneration = advanceSelection(authorizationId || '', uiModeRef.current);
      }
    }

    // Load existing conversation
    await loadConversation(threadId, authorizationId, selectionGeneration, uiModeRef.current);
  }, [advanceSelection, appState.threads, onViewChange, uiMode]);

  // Force refresh threads (for refresh button)
  const refreshThreads = useCallback(async (): Promise<UserThread[]> => {
    console.log('[ChatContainer] Force refreshing threads');
    return await loadUserThreads(true);
  }, []);

  const loadConversation = async (
    threadId: string,
    authorizationId: string | undefined,
    selectionGeneration: number,
    selectionMode: string,
  ) => {
    if (loadingRef.current) {
      console.log('[ChatContainer] Already loading conversation, skipping');
      return;
    }

    loadingRef.current = true;
    console.log('[ChatContainer] Starting to load conversation for thread:', threadId, 'clearing grading data and document selection');
    setTodos([]);
    setAppState(prev => ({
      ...prev,
      isLoadingConversation: true,
      loadingConversationError: null,
      selectedThreadId: threadId,
      // Clear document selection when switching threads
      selectedDocument: null,
      selectedDocuments: [],
      // Clear grading data immediately when switching threads
      gradeData: [],
      gradingLoading: false,
      gradingError: null,
    }));

    try {
      // Load conversation messages
      const conversationResponse = await ChatAPI.getThreadMessages(threadId);
      const newAuthId = authorizationId || '';
      if (!isCurrentSelection(newAuthId, selectionMode, selectionGeneration)) return;
      
      // Convert ThreadMessages to ChatMessages
      const chatMessages: ChatMessage[] = conversationResponse.messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp || undefined, // Convert null to undefined
      }));
      
      // Update authorization if provided
      const sessionId = getStoredSessionId(threadId) || crypto.randomUUID();
      
      setAppState(prev => ({
        ...prev,
        authorizationId: newAuthId,
        chatState: {
          ...prev.chatState,
          messages: chatMessages,
          threadId: threadId,
          sessionId,
          authNumber: newAuthId,
          error: null,
        },
        isLoadingConversation: false,
        statusMessage: newAuthId ? `Loaded conversation with authorization: ${newAuthId}` : 'Loaded conversation (no authorization number)',
      }));
      
      // Clear status message after 3 seconds
      setTimeout(() => {
        if (isCurrentSelection(newAuthId, selectionMode, selectionGeneration)) {
          setAppState(prev => ({ ...prev, statusMessage: '' }));
        }
      }, 3000);

      // Load grading data for this thread
      loadGradingData(threadId);

      // Load documents if authorization is available, otherwise clear them
      if (newAuthId.trim()) {
        await loadDocuments(newAuthId, false, selectionGeneration, selectionMode);
      } else {
        // Clear documents when there's no authorization ID
        setAppState(prev => ({
          ...prev,
          documents: [],
          selectedDocument: null,
          selectedDocuments: [], // Clear selected documents
          documentsLoading: false,
          documentsError: null,
          conversationLinks: [],
          // Note: Don't clear grading data here since we already loaded it for the thread above
          isDocumentPanelOpen: false,
        }));
      }

    } catch (error) {
      if (!isCurrentSelection(authorizationId || '', selectionMode, selectionGeneration)) return;
      console.error('[ChatContainer] Failed to load conversation:', error);
      setAppState(prev => ({
        ...prev,
        isLoadingConversation: false,
        loadingConversationError: error instanceof Error ? error.message : 'Failed to load conversation',
        statusMessage: '',
      }));
    } finally {
      loadingRef.current = false;
    }
  };

  // ==================== DOCUMENT MANAGEMENT ====================
  
  const loadDocuments = useCallback(async (
    authId: string,
    isNewConversation: boolean,
    expectedGeneration = selectionGenerationRef.current,
    expectedMode = uiModeRef.current,
  ) => {
    if (!authId.trim()) return;

    if (!isCurrentSelection(authId, expectedMode, expectedGeneration)) return;
    const currentUiMode = normalizeUiMode(expectedMode);
    console.log(`[ChatContainer] Loading documents for: ${authId}`);
    console.log(`[ChatContainer] Using uiMode: ${currentUiMode}`);
    
    setAppState(prev => ({
      ...prev,
      documentsLoading: true,
      documentsError: null,
      statusMessage: `Loading documents for authorization: ${authId}`,
      ...(isNewConversation ? { documents: [], selectedDocument: null, selectedDocuments: [] } : {}),
    }));

    try {
      const response = await ChatAPI.getDocuments(authId, currentUiMode?.toLowerCase() || 'authorization');
      if (!isCurrentSelection(authId, expectedMode, expectedGeneration)) return;
      const docCount = response.documents?.length || 0;
      setAppState(prev => ({
        ...prev,
        documents: response.documents || [],
        documentsLoading: false,
        statusMessage: `Loaded ${docCount} document${docCount !== 1 ? 's' : ''} for authorization: ${authId}`,
        // Auto-open panel when documents are loaded
        isDocumentPanelOpen: true,
      }));
      
      // Clear status message after 3 seconds
      setTimeout(() => {
        if (isCurrentSelection(authId, expectedMode, expectedGeneration)) {
          setAppState(prev => ({ ...prev, statusMessage: '' }));
        }
      }, 3000);
    } catch (error) {
      if (!isCurrentSelection(authId, expectedMode, expectedGeneration)) return;
      console.error('[ChatContainer] Failed to load documents:', error);
      setAppState(prev => ({
        ...prev,
        documentsLoading: false,
        documentsError: error instanceof Error ? error.message : 'Failed to load documents',
        statusMessage: '',
      }));
    }
  }, [isCurrentSelection]);

  const handleDocumentSelect = useCallback((document: DocumentInfo | null) => {
    setAppState(prev => ({
      ...prev,
      selectedDocument: document,
    }));
  }, []);

  const handleSelectedDocumentsChange = useCallback((documentGuids: string[]) => {
    setAppState(prev => ({
      ...prev,
      selectedDocuments: documentGuids,
    }));
  }, []);

  // ==================== GRADING MANAGEMENT ====================
  
  const loadGradingData = useCallback(async (threadId: string, turnCorrelationId?: string): Promise<GradeData[]> => {
    if (!threadId.trim()) {
      console.log('[ChatContainer] No thread ID provided for grading data');
      return [];
    }

    try {
      console.log('[ChatContainer] Starting to load grading data for thread:', threadId);
      setAppState(prev => ({
        ...prev,
        gradingLoading: true,
        gradingError: null,
      }));

      console.log('[ChatContainer] Making API call for grading data, thread:', threadId);
      const response = await ChatAPI.getGradingData(threadId, { turnCorrelationId });
      
      const grades: GradeData[] = (response.grades || []).map(grade => ({
        user_question: grade.question,
        score: grade.score,
        reasoning: grade.rationale,
        timestamp: grade.created_at ? Date.parse(grade.created_at) / 1000 : 0,
        evaluator: grade.evaluator,
        turn_correlation_id: grade.turn_correlation_id,
        conversation_turn: grade.conversation_turn,
        metric_name: grade.metric_name,
      }));
      
      console.log(`[ChatContainer] Successfully loaded ${grades.length} grading records for thread:`, threadId);
      setAppState(prev => ({
        ...prev,
        gradeData: turnCorrelationId
          ? [...prev.gradeData.filter(grade => grade.turn_correlation_id !== turnCorrelationId), ...grades]
          : grades,
        gradingLoading: false,
        gradingError: null,
      }));

      console.log('[ChatContainer] Loaded grading data:', grades);
      return grades;

    } catch (error) {
      console.error('[ChatContainer] Failed to load grading data for thread:', threadId, 'Error:', error);
      setAppState(prev => ({
        ...prev,
        gradeData: [],
        gradingLoading: false,
        gradingError: error instanceof Error ? error.message : 'Failed to load grading data',
      }));
      return [];
    }
  }, []);

  const handleGradeDataReceived = useCallback((gradeData: any) => {
    console.log('[ChatContainer] Received real-time grading data:', gradeData);
    
    // Process the grading data received from streaming
    if (gradeData && typeof gradeData === 'object') {
      const newGrade: GradeData = {
        user_question: gradeData.user_question || '',
        score: gradeData.score || 0,
        reasoning: gradeData.reasoning || '',
        timestamp: gradeData.timestamp || Date.now() / 1000,
        evaluator: gradeData.evaluator || 'AI Evaluator',
      };

      setAppState(prev => ({
        ...prev,
        gradeData: [...prev.gradeData, newGrade],
      }));
    }
  }, []);

  // ==================== CHAT MANAGEMENT ====================
  
  const updateChatState = useCallback((updater: (prev: ChatState) => ChatState) => {
    setAppState(prev => ({
      ...prev,
      chatState: updater(prev.chatState),
    }));
  }, []);

  const updateInputValue = useCallback((value: string) => {
    setAppState(prev => ({
      ...prev,
      inputValue: value,
    }));
  }, []);

  const updateStreamingMessage = useCallback((message: string) => {
    setAppState(prev => ({
      ...prev,
      streamingMessage: message,
    }));
  }, []);

  const updateStatusMessage = useCallback((message: string) => {
    setAppState(prev => ({
      ...prev,
      statusMessage: message,
    }));
  }, []);

  const updateConversationLinks = useCallback((links: ExtractedLink[]) => {
    setAppState(prev => ({
      ...prev,
      conversationLinks: links,
      // Auto-open panel when links are found
      isDocumentPanelOpen: prev.isDocumentPanelOpen || links.length > 0,
    }));
  }, []);

  // ==================== UI STATE MANAGEMENT ====================
  
  const handlePanelToggle = useCallback(() => {
    setAppState(prev => ({
      ...prev,
      isDocumentPanelOpen: !prev.isDocumentPanelOpen,
    }));
  }, []);

  const handlePanelClose = useCallback(() => {
    setAppState(prev => ({
      ...prev,
      isDocumentPanelOpen: false,
    }));
  }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setAppState(prev => ({
      ...prev,
      isResizing: true,
    }));
  }, []);

  const updatePanelWidth = useCallback((width: number) => {
    setAppState(prev => ({
      ...prev,
      panelWidth: Math.max(300, Math.min(width, window.innerWidth * 0.7)),
    }));
  }, []);

  // Handle panel resizing
  useEffect(() => {
    if (!appState.isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      updatePanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setAppState(prev => ({
        ...prev,
        isResizing: false,
      }));
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [appState.isResizing, updatePanelWidth]);

  // ==================== INITIALIZATION ====================
  
  // Track previous uiMode to detect changes
  const prevUiModeRef = useRef<string | undefined>(uiMode);
  
  useEffect(() => {
    // Load threads only once on mount
    loadUserThreads(false);
  }, []); // Empty dependency array - only run once
  
  useEffect(() => {
    // Clear context when switching between Authorizations and Appeals
    if (prevUiModeRef.current !== undefined && prevUiModeRef.current !== uiMode) {
      advanceSelection('', uiMode);
      console.log(`[ChatContainer] uiMode changed from ${prevUiModeRef.current} to ${uiMode}, clearing context`);
      
      // Clear all domain-specific state
      setAppState(prev => ({
        ...prev,
        // Clear authorization/complaint ID
        authorizationId: '',
        // Clear chat state
        chatState: {
          ...prev.chatState,
          authNumber: '',
          messages: [],
          threadId: undefined,
          sessionId: crypto.randomUUID(),
          error: null,
          isLoading: false,
          isStreaming: false,
        },
        // Clear documents
        documents: [],
        selectedDocument: null,
        selectedDocuments: [],
        documentsLoading: false,
        documentsError: null,
        // Clear threads
        threads: [],
        selectedThreadId: null,
        threadsLoading: false,
        // Clear conversation links
        conversationLinks: [],
        // Clear grading data
        gradeData: [],
        gradingLoading: false,
        gradingError: null,
        // Clear input/status
        inputValue: '',
        streamingMessage: '',
        statusMessage: '',
        // Close document panel
        isDocumentPanelOpen: false,
      }));
      setTodos([]);
      
      // Reload threads for the new domain
      loadUserThreads(true);
    }
    prevUiModeRef.current = uiMode;
  }, [advanceSelection, uiMode, loadUserThreads]); // Reload when domain changes
  
  useEffect(() => {
    // Check AI services status on mount
    const checkAIServices = async () => {
      try {
        const response = await ChatAPI.healthCheck();
        updateChatState(prev => ({
          ...prev,
          aiServicesAvailable: response.status === 'healthy',
        }));
      } catch (error) {
        console.error('[ChatContainer] Failed to check AI services:', error);
        updateChatState(prev => ({
          ...prev,
          aiServicesAvailable: false,
        }));
      }
    };

    checkAIServices();
  }, [updateChatState]);

  // ==================== RENDER ====================
  
  return (
    <div className={classes.container}>
      {/* Header with Controls */}
      <header className={classes.header}>
        <HeaderControls
          authorizationId={appState.authorizationId}
          selectedThreadId={appState.selectedThreadId}
          threads={appState.threads}
          threadsLoading={appState.threadsLoading}
          isLoadingConversation={appState.isLoadingConversation}
          onAuthorizationChange={handleAuthorizationChange}
          onThreadSelect={handleThreadSelect}
          onRefreshThreads={refreshThreads}
          uiMode={uiMode}
        />
      </header>

      {/* Main Content */}
      <div className={classes.content}>
        {/* Chat Area */}
        <div className={classes.chatArea}>
          <ChatInterface
            chatState={appState.chatState}
            inputValue={appState.inputValue}
            streamingMessage={appState.streamingMessage}
            statusMessage={appState.statusMessage}
            isLoadingConversation={appState.isLoadingConversation}
            loadingConversationError={appState.loadingConversationError}
            gradeData={appState.gradeData}
            selectedDocuments={appState.selectedDocuments}
            uiMode={uiMode}
            todos={todos}
            onTodosChange={setTodos}
            onChatStateChange={updateChatState}
            onInputValueChange={updateInputValue}
            onStreamingMessageChange={updateStreamingMessage}
            onStatusMessageChange={updateStatusMessage}
            onConversationLinksChange={updateConversationLinks}
            onLoadDocuments={(authId: string) => loadDocuments(authId, true)}
            onGradeReceived={handleGradeDataReceived}
            onRefreshGrades={loadGradingData}
          />
        </div>

        {/* Document Panel */}
        {appState.isDocumentPanelOpen && (
          <div className={classes.documentPanelContainer}>
            <DocumentPanel
              isOpen={appState.isDocumentPanelOpen}
              onToggle={handlePanelToggle}
              onClose={handlePanelClose}
              authorizationId={appState.authorizationId}
              selectedDocument={appState.selectedDocument}
              onDocumentSelect={handleDocumentSelect}
              selectedDocuments={appState.selectedDocuments}
              onSelectedDocumentsChange={handleSelectedDocumentsChange}
              documents={appState.documents}
              documentsLoading={appState.documentsLoading}
              documentsError={appState.documentsError}
              conversationLinks={appState.conversationLinks}
              gradeData={appState.gradeData}
              panelWidth={appState.panelWidth}
              isResizing={appState.isResizing}
              handleResizeStart={handleResizeStart}
              className={classes.documentPanel}
              uiMode={uiMode}
              classes={{
                documentPanel: classes.documentPanel || '',
                resizeHandle: classes.resizeHandle || '',
                resizeHandleActive: classes.resizeHandleActive || '',
                panelToggleButton: classes.panelToggleButton || '',
                documentPanelHeader: classes.documentPanelHeader || '',
                documentPanelContent: classes.documentPanelContent || '',
                documentViewerContainer: classes.documentViewerContainer || '',
                documentListContainer: classes.documentListContainer || '',
                documentPanelSplitter: classes.documentPanelSplitter || '',
              }}
            />
          </div>
        )}
      </div>

      {/* External toggle button when panel is closed */}
      {!appState.isDocumentPanelOpen && (appState.authorizationId?.trim() || appState.conversationLinks.length > 0) && (
        <Button
          className={classes.panelToggleButtonExternal}
          appearance="secondary"
          icon={<ChevronLeftRegular />}
          onClick={handlePanelToggle}
          title="Open document panel"
        />
      )}
    </div>
  );
};

export default ChatContainer;